import {
  CartesianGrid,
  Line,
  LineChart as RLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReactNode } from 'react';
import { palette } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

interface SeriesDef {
  key: string;
  label: string;
  color?: string;
  strokeDasharray?: string;
}

interface LineChartProps<T extends object> {
  data: T[];
  xKey: keyof T & string;
  series: SeriesDef[];
  formatValue?: (v: number) => ReactNode;
  formatX?: (v: string) => string;
  height?: number;
}

export function LineChart<T extends object>({
  data,
  xKey,
  series,
  formatValue,
  formatX,
  height = 280,
}: LineChartProps<T>) {
  return (
    <div style={{ width: '100%', height }}>
      <ResponsiveContainer>
        <RLineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="2 4" stroke={palette.grid()} vertical={false} />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatX}
            stroke={palette.axis()}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
          />
          <YAxis
            stroke={palette.axis()}
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            width={48}
          />
          <Tooltip
            cursor={{ stroke: palette.border(), strokeWidth: 1 }}
            content={(props) => <ChartTooltip {...props} formatValue={formatValue} />}
          />
          {series.map((s, i) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color ?? palette.series[i % palette.series.length]()}
              strokeWidth={2}
              strokeDasharray={s.strokeDasharray}
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive={false}
            />
          ))}
        </RLineChart>
      </ResponsiveContainer>
    </div>
  );
}
